/**
 * PRICING CONFIGURATION
 * ─────────────────────────────────────────────────────────────
 * Single source of truth for every price shown on the site.
 *
 * How it works:
 * - Egyptian visitors  → see the EGP price (`egp`)
 * - Everyone else      → see the international USD price (`usd`)
 *
 * The USD numbers are NOT a raw FX conversion of the EGP price.
 * They are positioned as an **international / specialist rate**
 * (roughly 3–5× the raw FX value). Adjust freely — nothing else
 * in the code needs to change.
 *
 * To change a price:
 *   1. Update the number(s) below.
 *   2. Save. That's it.
 */

export type CurrencyCode = "EGP" | "USD";

export interface PriceEntry {
  /** Egyptian Pounds — what local visitors pay. */
  egp: number;
  /** US Dollars — the international / specialist rate. */
  usd: number;
  /** Optional strike-through anchor price (before discount). */
  originalEgp?: number;
  originalUsd?: number;
}

/**
 * Every price on the site, keyed by the EGP amount already used
 * throughout the codebase. This keeps existing translation strings
 * and section props working without a refactor.
 */
export const PRICES: Record<number, PriceEntry> = {
  // Foundation session (40 min discovery call)
  500: { egp: 500, usd: 49 },

  // Starter package
  3500: { egp: 3500, usd: 349, originalEgp: 5000, originalUsd: 499 },

  // Transformation package (most popular)
  9000: { egp: 9000, usd: 899, originalEgp: 12857, originalUsd: 1299 },

  // Elite package (VIP)
  25000: { egp: 25000, usd: 2499, originalEgp: 35714, originalUsd: 3499 },
};

export const CURRENCY_META: Record<CurrencyCode, {
  code: CurrencyCode;
  symbol: string;
  /** Symbol shown after the number (e.g. "جنيه"). */
  nameAr: string;
  nameEn: string;
  /** Locale used for number grouping. */
  locale: string;
}> = {
  EGP: { code: "EGP", symbol: "ج.م", nameAr: "جنيه", nameEn: "EGP", locale: "ar-EG" },
  USD: { code: "USD", symbol: "$",    nameAr: "دولار", nameEn: "USD", locale: "en-US" },
};

/** Format a raw amount + currency code as a locale-aware string. */
export function formatAmount(
  amount: number,
  currency: CurrencyCode,
  lang: "ar" | "en",
): string {
  const meta = CURRENCY_META[currency];
  const localeNumber = lang === "ar"
    ? amount.toLocaleString(meta.locale)
    : amount.toLocaleString("en-US");

  if (currency === "USD") {
    // "$1,299" reads clean in every language
    return `$${localeNumber}`;
  }
  // EGP → "1,299 جنيه" / "1,299 EGP"
  return `${localeNumber} ${lang === "ar" ? meta.nameAr : meta.nameEn}`;
}

/**
 * Look up the visitor-facing price for a given EGP anchor amount.
 * Falls back to a plain FX-style conversion (18× → ~USD) if the
 * amount isn't listed above — safe default so nothing renders blank.
 */
export function getVisitorPrice(
  egpAmount: number,
  currency: CurrencyCode,
): number {
  const entry = PRICES[egpAmount];
  if (entry) return currency === "USD" ? entry.usd : entry.egp;
  if (currency === "USD") return Math.max(1, Math.round(egpAmount / 18));
  return egpAmount;
}

export function getVisitorOriginalPrice(
  egpAmount: number,
  currency: CurrencyCode,
): number | undefined {
  const entry = PRICES[egpAmount];
  if (!entry) return undefined;
  if (currency === "USD") return entry.originalUsd;
  return entry.originalEgp;
}
